// lib/jobs.ts
const createAbortError = (): Error => {
  const error = new Error('Operation aborted.');
  error.name = 'AbortError';
  return error;
};

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw createAbortError();
  }
};

const waitWithAbort = (ms: number, signal?: AbortSignal): Promise<void> => (
  new Promise<void>((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleAbort = () => {
      clearTimeout(timeoutId);
      reject(createAbortError());
    };

    timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, ms);

    if (signal) {
      signal.addEventListener('abort', handleAbort, { once: true });
    }
  })
);

/**
 * A utility function to retry an async operation with exponential backoff.
 * @param fn The async function to execute.
 * @param options Configuration for retries, delay, and backoff.
 * @returns The result of the async function.
 * @throws The last error if all retries fail.
 */
export const retry = async <T>(
  fn: () => Promise<T>,
  options: { retries: number; delay: number; backoff?: boolean; signal?: AbortSignal }
): Promise<T> => {
  let lastError: Error | undefined;
  let currentDelay = options.delay;

  for (let i = 0; i < options.retries; i++) {
    throwIfAborted(options.signal);
    try {
      const result = await fn();
      throwIfAborted(options.signal);
      return result;
    } catch (error) {
      if (
        options.signal?.aborted ||
        (error as { name?: string })?.name === 'AbortError' ||
        (error as { name?: string })?.name === 'CanceledError' ||
        (error as { code?: string })?.code === 'ERR_CANCELED'
      ) {
        throw createAbortError();
      }
      lastError = error as Error;
      console.warn(`Attempt ${i + 1} of ${options.retries} failed: ${lastError.message}. Retrying in ${currentDelay}ms...`);
      await waitWithAbort(currentDelay, options.signal);
      if (options.backoff) {
        currentDelay *= 2; // Exponential backoff
      }
    }
  }
  console.error(`All ${options.retries} retry attempts failed.`);
  throw lastError;
};
